// US Heatmap Component extending MapComponent functionality
import { BaseMapUtil } from './baseMapUtil.js';

export default function createHeatMapComponent(instanceId = '') {
	return {
		template: instanceId ? '#heat-map-template-' + instanceId : '#heat-map-template',
		props: ['heatmapData', 'colorScale', 'modalData', 'refinementItems', 'indexName'],
		emits: ['state-clicked', 'modal-opened'],
		data() {
			return {
				usStatesData: null,
				statesSelection: null,
				svgSelection: null,
				pathGenerator: null,
				showModal: false,
				modalContent: null,
				openSections: {},
				colorThresholds: null,
				instanceId: instanceId,
				mapContext: null,
				defaultColors: {
					noData: 'rgba(31, 52, 90, 0.2)',
					low: 'rgba(50, 147, 111, 0.3)',
					medium: 'rgba(50, 147, 111, 0.5)', 
					high: 'rgba(50, 147, 111, 0.7)',
					veryHigh: 'rgba(50, 147, 111, 1.0)'
				},
				granteeColors: {
					noData: '#f0f0f0',
					low: 'rgba(255, 191, 0, 0.2)',
					medium: 'rgba(255, 191, 0, 0.4)',
					high: 'rgba(255, 191, 0, 0.6)',
					veryHigh: 'rgba(255, 191, 0, 0.8)'
				}
			}
		},
		computed: {
			hasActiveRefinements() {
				if (!this.refinementItems || !this.refinementItems.length) {
					return false;
				}
				
				// Check if any boolean refinement is active
				return this.refinementItems.some(item => {
					return item.attribute.endsWith('_bool') && 
						   item.refinements && 
						   item.refinements.some(ref => ref.value === 'true');
				});
			},
			currentColors() {
				if (this.indexName === 'compass_asa_grantee') {
					return this.granteeColors;
				}
				return this.defaultColors;
			}
		},
		mounted() {
			this.initializeMap();
			// Add escape key listener for modal accessibility
			document.addEventListener('keydown', this.handleKeydown);
		},
		beforeUnmount() {
			// Clean up event listener
			document.removeEventListener('keydown', this.handleKeydown);
			
			// Clean up map resources
			if (this.mapContext && this.mapContext.cleanup) {
				this.mapContext.cleanup();
			}
		},
		updated() {
			// Force color update after component updates
			this.$nextTick(() => {
				if (this.heatmapData && this.statesSelection) {
					this.calculateThresholds(this.heatmapData);
					this.updateHeatmapColors(this.heatmapData);
				}
			});
		},
		watch: {
			heatmapData: {
				handler(newData) {
					this.calculateThresholds(newData);
					this.updateHeatmapColors(newData);
				},
				deep: true
			},
			hasActiveRefinements: {
				handler(newValue, oldValue) {
					// Re-initialize map when refinements become active after being inactive
					if (newValue && !oldValue) {
						this.$nextTick(() => {
							this.initializeMap();
						});
					}
				}
			}
		},
		methods: {
			calculateThresholds(data) {
				if (!data) {
					this.colorThresholds = null;
					return;
				}
				// Extract all numeric values (including zero)
				const values = Object.values(data)
					.map(item => {
						const value = typeof item === 'object' ? item.total_count : item;
						return typeof value === 'number' ? value : null;
					})
					.filter(value => value !== null)
					.sort((a, b) => a - b);

				if (values.length === 0) {
					this.colorThresholds = null;
					return;
				}

				const min = Math.min(...values);
				const max = Math.max(...values);
				const range = max - min;

				// Use percentage-based thresholds (0-100%)
				if (range === 0) {
					// All values are the same
					this.colorThresholds = {
						low: 25,
						medium: 50,
						high: 75,
						veryHigh: 100,
						min: min,
						max: max
					};
				} else {
					// Fixed percentage thresholds
					this.colorThresholds = {
						low: 25,
						medium: 50,
						high: 75,
						veryHigh: 100,
						min: min,
						max: max
					};
				}
			},

			async initializeMap() {
				const mapContext = await BaseMapUtil.initializeBaseMap({
					containerId: this.instanceId ? 'heatmap-container-' + this.instanceId : 'heatmap-container',
					svgId: this.instanceId ? 'heatmap-svg-' + this.instanceId : 'heatmap-svg',
					errorPrefix: 'Heatmap',
					enableZoom: false,
					onStateClick: (event, d) => {
						this.onStateClick(d);
					},
					getStateColor: (stateName) => this.getStateColor(stateName),
					onStateHover: (event, d) => {
						const currentFill = d3.select(event.currentTarget).attr('fill');
						if (currentFill && d3.color(currentFill)) {
							d3.select(event.currentTarget)
								.attr('data-original-fill', currentFill)
								.attr('fill', d3.color(currentFill).darker(0.3));
						}
					},
					onStateMouseOut: (event, d) => {
						const originalFill = d3.select(event.currentTarget).attr('data-original-fill');
						if (originalFill) {
							d3.select(event.currentTarget)
								.attr('fill', originalFill)
								.attr('data-original-fill', null);
						}
					},
					afterStatesDrawn: ({ svgSelection, width, height }) => {
						this.addLegend(svgSelection, width, height);
					}
				});

				if (mapContext) {
					// Store the entire map context
					this.mapContext = mapContext;
					
					// Store references for backward compatibility
					this.svgSelection = mapContext.svgSelection;
					this.pathGenerator = mapContext.pathGenerator;
					this.statesSelection = mapContext.statesSelection;
					this.usStatesData = mapContext.usStatesData;
					
					// If heatmap data already exists, calculate thresholds and update colors
					if (this.heatmapData) {
						this.calculateThresholds(this.heatmapData);
						this.updateHeatmapColors(this.heatmapData);
					}
				}
			},
			
			onStateClick(stateData) {
				const stateName = stateData.properties.name;
				const stateValue = this.getStateValue(stateName);

				// Track Compass heatmap state clicks - Key KPI
				if (typeof gtag !== 'undefined') {
					gtag('event', 'select_state', {
						content_type: 'heatmap_state',
						tool_type: this.indexName,
						state_name: stateName,
						state_value: stateValue,
						state_abbreviation: stateData.properties.abbreviation || 'N/A',
						context: this.indexName + '_heatmap_interaction'
					});
				}

				// Prepare modal content
				const modalContent = {
					stateName: stateName,
					value: stateValue,
					...stateData.properties,
					...this.heatmapData[stateName]
				};
				
				// Open modal with content (this will set modalContent and openSections)
				this.openModal(modalContent);
				
				// Emit events for parent component to handle
				this.$emit('state-clicked', {
					state: stateName,
					value: stateValue,
					data: modalContent
				});
				
				this.$emit('modal-opened', modalContent);
			},
			
			getStateColor(stateName) {
				const value = this.getStateValue(stateName);
				
				if (value === null || value === undefined) {
					return this.currentColors.noData;
				}
				
				// Use custom color scale if provided, otherwise use percentage ranges
				if (this.colorScale && typeof this.colorScale === 'function') {
					return this.colorScale(value);
				}
				
				// Return no data color for zero values
				if (value === 0) return this.currentColors.noData;
				
				// Use percentage-based thresholds if available
				if (!this.colorThresholds) {
					// Fallback to default if thresholds not calculated yet
					return this.currentColors.medium;
				}
				// console.log(stateName, value.total_count, this.colorThresholds);
				// Convert value to percentage based on min/max range
				const { min, max } = this.colorThresholds;
				const percentage = max === min ? 100 : ((value - min) / (max - min)) * 100;
				
				// Apply percentage-based color thresholds
				if (percentage <= 25) return this.currentColors.low;
				if (percentage <= 50) return this.currentColors.medium;
				if (percentage <= 75) return this.currentColors.high;
				return this.currentColors.veryHigh;
			},
			
			getStateValue(stateName) {
				if(this.indexName === 'compass_asa_grantee'){
					//national grants apply to all states so add one to each
					if (!this.heatmapData || !this.heatmapData[stateName]) {
						return 1;
					}
					return this.heatmapData[stateName].total_count+1 || 1;
	
				}else{
					if (!this.heatmapData || !this.heatmapData[stateName]) {
						return 0;
					}
					return this.heatmapData[stateName].total_count || 0;
				}
			},
			
			updateHeatmapColors(newData) {
				if (!this.statesSelection || !newData) {
					return;
				}
				
				// Update all state colors based on new data
				this.statesSelection.attr('fill', (d) => {
					const color = this.getStateColor(d.properties.name);
					return color;
				});
				
				// Update small states legend colors
				if (this.svgSelection) {
					this.svgSelection.selectAll('.state-legend .legend-item .legend-box')
						.attr('fill', (d) => {
							// d.name contains the state name from the bound data
							return d.name ? this.getStateColor(d.name) : '#f0f0f0';
						});
				}
				
				// Update legend with new thresholds
				this.updateLegend();
			},
			
			getLegendData() {
				// Always show percentage-based legend
				return [
					{ label: 'No Data', color: this.currentColors.noData },
					{ label: '0-25%', color: this.currentColors.low },
					{ label: '26-50%', color: this.currentColors.medium },
					{ label: '51-75%', color: this.currentColors.high },
					{ label: '76-100%', color: this.currentColors.veryHigh }
				];
			},

			addLegend(svgSelection, width, height) {
				return false;
				const legendData = this.getLegendData();
				
				const legend = svgSelection.append('g')
					.attr('class', 'heatmap-legend')
					.attr('transform', `translate(20, ${height - 150})`);
				
				// Legend background
				legend.append('rect')
					.attr('x', -10)
					.attr('y', -10)
					.attr('width', 120)
					.attr('height', legendData.length * 25 + 20)
					.attr('fill', 'rgba(255, 255, 255, 0.9)')
					.attr('stroke', '#cccccc')
					.attr('rx', 4);
				
				// Legend items
				const legendItems = legend.selectAll('.legend-item')
					.data(legendData)
					.enter()
					.append('g')
					.attr('class', 'legend-item')
					.attr('transform', (d, i) => `translate(0, ${i * 25})`);
				
				legendItems.append('rect')
					.attr('width', 18)
					.attr('height', 18)
					.attr('fill', d => d.color)
					.attr('stroke', '#ffffff');
				
				legendItems.append('text')
					.attr('x', 25)
					.attr('y', 9)
					.attr('dy', '0.35em')
					.attr('fill', '#333333')
					.attr('font-size', '12px')
					.text(d => d.label);
			},

			updateLegend() {
				if (!this.svgSelection) return;

				// Remove existing legend
				// this.svgSelection.select('.heatmap-legend').remove();

				// Get current SVG dimensions
				const svgElement = this.svgSelection.node();
				const width = svgElement.clientWidth || 800;
				const height = svgElement.clientHeight || 500;

				// Add updated legend
				// this.addLegend(this.svgSelection, width, height);
			},
			
			openModal() {
				this.showModal = true;
				// Add modal backdrop click handler
				this.$nextTick(() => {
					const modal = document.querySelector('.heatmap-modal');
					if (modal) {
						modal.addEventListener('click', (e) => {
							if (e.target === modal) {
								this.closeModal();
							}
						});
					}
				});
			},
			
			closeModal() {
				this.showModal = false;
				this.modalContent = null;
				this.openSections = {};
			},
			
			toggleAccordion(section) {
				this.openSections[section] = !this.openSections[section];
			},
			
			openModal(content) {
				this.modalContent = content;
				this.showModal = true;
				// Reset accordion sections to closed when modal opens
				// Dynamically create openSections based on the content's taxonomy keys
				const newOpenSections = {};
				if (content) {
					Object.keys(content).forEach(key => {
						// Only add keys that look like taxonomy data (arrays of terms)
						if (Array.isArray(content[key]) && content[key].length > 0 && 
							content[key][0]?.term_id !== undefined) {
							newOpenSections[key] = false;
						}
					});
				}
				this.openSections = newOpenSections;
			},
			
			getRowClass(taxonomyName, termId) {
				if (!this.modalContent || !this.modalContent[taxonomyName]) {
					return '';
				}
				
				// Find the index of this term in the visible items for this taxonomy
				const visibleTerms = this.modalContent[taxonomyName] || [];
				const termIndex = visibleTerms.findIndex(item => item.term_id == termId);
				
				// Return alternating background color class
				return termIndex % 2 === 0 ? 'bg-[#1F345A33]' : 'bg-[#1F345A0D]';
			},
			
			getRowClassNotOffered(taxonomyName, termId) {
				return 'bg-white';
			},
			exportData() {
				if (!this.modalContent || !this.modalContent.stateName) {
					return;
				}
				
				const csvRows = [];
				const stateName = this.modalContent.stateName;
				
				// Add header
				csvRows.push(['State', 'Category', 'Term Name', 'Description']);
				
				// Define taxonomy labels for better CSV headers
				const taxonomyLabels = {
					'middle_school': 'Middle School Career Exploration',
					'high_school': 'High School Work-Based Learning (WBL)', 
					'post_secondary': 'Postsecondary Education/Career Navigation'
				};
				
				// Process each taxonomy in the modal content
				Object.keys(this.modalContent).forEach(key => {
					// Only process taxonomy arrays
					if (Array.isArray(this.modalContent[key]) && 
						this.modalContent[key].length > 0 && 
						this.modalContent[key][0]?.term_id !== undefined) {
						
						const categoryName = taxonomyLabels[key] || key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
						
						this.modalContent[key].forEach(term => {
							const description = (term.description || '').replace(/"/g, '""'); // Escape quotes
							csvRows.push([
								`"${stateName}"`,
								`"${categoryName}"`, 
								`"${term.name}"`,
								`"${description}"`
							]);
						});
					}
				});
				
				// Convert to CSV string
				const csvContent = csvRows.map(row => row.join(',')).join('\n');
				
				// Create and download file
				const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
				const link = document.createElement('a');
				const url = URL.createObjectURL(blob);
				link.setAttribute('href', url);
				link.setAttribute('download', `${stateName.replace(/\s+/g, '_')}_Career_Data.csv`);
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			},
			
			handleKeydown(event) {
				// Close modal when Escape key is pressed
				if (event.key === 'Escape' && this.showModal) {
					this.closeModal();
				}
			},
			
			isFilterActive(taxonomyName) {
				// Check if the corresponding boolean filter is active using refinementItems prop
				const filterKey = taxonomyName + '_bool';
				
				if (this.refinementItems && this.refinementItems.length > 0) {
					// Find the refinement item for this filter
					const refinement = this.refinementItems.find(item => item.attribute === filterKey);
					if (refinement && refinement.refinements) {
						// Check if 'true' is in the active refinements
						return refinement.refinements.some(ref => ref.value === 'true');
					}
				}
				
				return false;
			}
		}
	};
}